// src/lib/invoiceDocuments.js
// Session 78n9.4, Issue #298 (inside #395). Gate screen 3 of
// docs/gates/Stede_DocumentOutput_Gate_395.html.
//
// WHAT THIS IS: the batch half of #298, and the half that has nothing to do
// with QuickBooks. Bridgette ticks rows on Invoicing and gets one of two
// artifacts from the same control -- a stack of per-invoice documents merged
// into a single PDF, or a one-page register listing what she selected.
//
// The competitive position, already researched in #298 and not re-argued here:
// Oracle Payables makes you submit two different reports from a different
// screen to get the register and the documents, and Coupa emails you a zip
// asynchronously. Both artifacts fall out of ONE multi-select here,
// synchronously, in the browser. That is available to Stede precisely because
// Stede is smaller -- Coupa's asynchronicity is a consequence of scale D&R
// does not have.
//
// THE RULE THIS FILE OBEYS, and it is purchaseOrderPdf.js's rule: nothing here
// invents a number. Line amounts come from material_request_invoice_lines,
// invoice totals from the invoice row, and the net of an invoice against its
// credits from vendorCredit.js -- which is already the single place that
// arithmetic lives. A document that computed its own totals would be a second
// opinion about what D&R owes.

import { money, documentDate } from './purchaseOrder';
import { isCredit, isInvoiceDoc } from './vendorCredit';
import { supabase } from './supabase';

// Invoicing's locked magenta, as the RGB triplet jsPDF wants.
export const INVOICING_ACCENT = '#B31F7C';
const ACCENT_RGB = [179, 31, 124];
const INK_RGB = [26, 26, 26];
const MUTED_RGB = [107, 107, 107];

// D5. The Ledger refuses above 25,000 rows; a batch of payables documents hits
// its real ceiling far sooner, and for a different reason -- these are PAGES,
// generated in a browser tab, and 400 of them is a frozen tab rather than a
// slow one. The number is deliberately a count of DOCUMENTS rather than of
// lines, because that is what the person selecting is counting.
export const DOCUMENT_BATCH_CAP = 150;
export const REGISTER_ROW_CAP = 1000;

function num(v) {
  if (v == null) return 0;
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

// ---------------------------------------------------------------------------
// The set the gate calls "Unexported obligations"
// ---------------------------------------------------------------------------

// The same predicate computeAging() narrows on, extracted so the strip and the
// list cannot drift apart about what "unexported" means. An invoice is exported
// once it carries either mark: exported_at is written by every export path,
// quickbooks_bill_id only by the push, and 78n9.3 checks both because a row
// pushed before exported_at existed carries only the second.
export function isUnexported(doc) {
  return !doc?.exported_at && !doc?.quickbooks_bill_id;
}

// ---------------------------------------------------------------------------
// One invoice, as three figures per line
// ---------------------------------------------------------------------------

// Gate screen 3's caption, and it is the load-bearing requirement of this file:
// ordered, received and invoiced stay THREE SEPARATE FIGURES. A single blended
// total on a payables document is the defect class that produced #279, where a
// line that had never been delivered reported its ordered quantity as received
// and reconciled clean.
//
// `invoiceId` scopes the billed quantity to THIS invoice rather than the line's
// lifetime, which is #283's rule: a line can be billed across two invoices, and
// showing the lifetime figure on one of them invites paying for it twice.
export function lineFigures(item, invoiceId) {
  const lines = (item?.material_request_invoice_lines || [])
    .filter(l => String(l.invoice_id) === String(invoiceId));

  return {
    ordered: num(item?.qty_requested),
    received: item?.qty_received == null ? null : num(item.qty_received),
    invoiced: lines.reduce((sum, l) => sum + num(l.qty_invoiced), 0),
    amount: lines.reduce((sum, l) => sum + num(l.line_amount), 0),
    hasLine: lines.length > 0,
  };
}

export function lineLabel(item) {
  if (item?.is_off_catalog) return item.off_catalog_description || 'Off-catalog item';
  return item?.inventory_items?.name || 'Item';
}

// A model, not a document. Everything the PDF and the register both need, built
// once so the two artifacts cannot disagree about the same selection -- which
// is #418's stated worry about two export paths, answered here before there are
// two of them.
export function buildInvoiceDocument({ invoice, request, settings, branchName }) {
  const credit = isCredit(invoice);
  const allDocs = request?.material_request_invoices || [];

  // Only the lines this invoice actually billed for. An order line untouched by
  // this invoice is not on this invoice's document -- it belongs to whichever
  // invoice billed it, and listing it here at zero would read as a dispute.
  const items = (request?.material_request_items || [])
    .map(it => ({ item: it, figures: lineFigures(it, invoice?.id) }))
    .filter(x => x.figures.hasLine)
    .map(x => ({
      label: lineLabel(x.item),
      sku: x.item?.inventory_items?.sku || (x.item?.is_off_catalog ? 'Off-catalog' : ''),
      uom: x.item?.inventory_items?.uom || x.item?.off_catalog_uom || '',
      ...x.figures,
    }));

  // Every credit written against THIS invoice, so the document says what the
  // vendor billed and what they have since credited back rather than only the
  // first of those. netDue is the number a person acts on.
  const credits = allDocs.filter(d => isCredit(d)
    && String(d.credit_of_invoice_id) === String(invoice?.id));
  const creditedAmount = credits.reduce((sum, c) => sum + num(c.total_amount), 0);
  const total = num(invoice?.total_amount);

  return {
    id: invoice?.id,
    isCredit: credit,
    kind: credit ? 'Vendor credit' : 'Invoice',
    invoiceRef: invoice?.invoice_ref || '(no reference)',
    invoiceDate: invoice?.invoice_date || null,
    dueDate: invoice?.due_date || null,
    paymentTerms: invoice?.payment_terms || '',
    discrepancyFlagged: !!invoice?.discrepancy_flagged,
    discrepancyResolved: !!invoice?.discrepancy_resolved,

    vendorName: request?.inventory_vendors?.name || 'Unknown vendor',
    branchName: branchName || '',
    companyName: settings?.company_name || settings?.legal_name || '',

    requestNumber: request?.request_number || '',
    poRef: request?.po_ref || '',
    workOrderRef: request?.work_order_ref || '',

    items,
    subtotal: num(invoice?.subtotal),
    tax: num(invoice?.tax_amount),
    total,
    creditedAmount,
    // A credit's own "net due" is the credit itself; it is never reduced by
    // something else. Signing is left to the reader: this is stored positive
    // per #299 and every caller renders the minus sign.
    netDue: credit ? total : total - creditedAmount,
  };
}

// ---------------------------------------------------------------------------
// The register -- Oracle's artifact, produced from the same selection
// ---------------------------------------------------------------------------

// One row per document, grouped by vendor with subtotals, which is what Oracle's
// Invoice Register produces from a submitted request. Grouped rather than merely
// sorted because a subtotal is the only reason to group, and "how much do I owe
// Houston Sign Supply this month" is the question the artifact exists to answer.
//
// CREDITS ARE SUBTRACTED. Stored positive per #299, so the sign is applied here
// exactly once. A register that added them would report D&R owing MORE because
// a vendor credited money back, which is the same defect class as #318.
export function buildRegister(documents) {
  const byVendor = new Map();

  documents.forEach(d => {
    const key = d.vendorName;
    if (!byVendor.has(key)) byVendor.set(key, { vendorName: key, rows: [], subtotal: 0 });
    const group = byVendor.get(key);
    const signed = d.isCredit ? -d.total : d.total;
    group.rows.push({
      kind: d.kind,
      isCredit: d.isCredit,
      invoiceRef: d.invoiceRef,
      invoiceDate: d.invoiceDate,
      dueDate: d.dueDate,
      poRef: d.poRef,
      requestNumber: d.requestNumber,
      branchName: d.branchName,
      flagged: d.discrepancyFlagged && !d.discrepancyResolved,
      amount: d.total,
      signed,
    });
    group.subtotal += signed;
  });

  const vendors = [...byVendor.values()]
    .sort((a, b) => a.vendorName.localeCompare(b.vendorName));
  vendors.forEach(v => v.rows.sort((a, b) => String(a.invoiceDate || '').localeCompare(String(b.invoiceDate || ''))));

  return {
    vendors,
    grand: vendors.reduce((sum, v) => sum + v.subtotal, 0),
    documentCount: documents.length,
    invoiceCount: documents.filter(d => !d.isCredit).length,
    creditCount: documents.filter(d => d.isCredit).length,
  };
}

// The running total the selection bar shows, and the mobile bar's second half.
// Same signing rule as the register, from the same helper, because a total that
// disagreed with the register produced from it would be worse than no total.
export function selectionTotal(docs) {
  return (docs || []).reduce(
    (sum, d) => sum + (isCredit(d) ? -num(d.total_amount) : num(d.total_amount)),
    0
  );
}

// ---------------------------------------------------------------------------
// D5 -- count before exporting, and refuse with the number
// ---------------------------------------------------------------------------

// Never silently truncate. The Ledger's rule, applied to a smaller ceiling for a
// different reason, and the refusal names the figure so the person can act on it
// rather than guessing how much smaller "smaller" is.
export function batchRefusal(layout, count) {
  const cap = layout === 'register' ? REGISTER_ROW_CAP : DOCUMENT_BATCH_CAP;
  if (count <= cap) return null;
  return layout === 'register'
    ? `That selection is ${count} rows. The register holds ${cap}. Narrow the selection and run it again.`
    : `That selection is ${count} documents. A batch holds ${cap}, because each one is a page your browser has to draw. `
      + `Narrow the selection, or run the register instead — it takes the whole selection on one page.`;
}

// ---------------------------------------------------------------------------
// D12 -- the file states what it is, on its face
// ---------------------------------------------------------------------------

// The Ledger's describeExport(), in the terms this surface has. A batch of
// payables documents six months old has to be able to say whether it was every
// vendor or one, unexported or settled, and who produced it -- for the same
// reason the Ledger's CSV does. A file that cannot describe itself cannot be
// relied on as a record.
export function describeBatch({
  companyName, layout, scopeLabel, search, documentCount, grand, generatedBy, generatedAt,
}) {
  return [
    ['Document', layout === 'register' ? 'Payables register' : 'Payables documents'],
    ['Scope', scopeLabel],
    ['Search', search || '(none)'],
    ['Documents in this file', String(documentCount)],
    ['Net total', money(grand)],
    ['Generated by', generatedBy],
    ['Generated', generatedAt],
  ];
}

export function batchFilename(layout, generatedAt) {
  const stamp = (generatedAt instanceof Date ? generatedAt : new Date())
    .toISOString().slice(0, 10);
  return layout === 'register'
    ? `payables-register-${stamp}.pdf`
    : `payables-documents-${stamp}.pdf`;
}

// ---------------------------------------------------------------------------
// The PDFs
// ---------------------------------------------------------------------------

// jsPDF is imported DYNAMICALLY, matching every other PDF path in this codebase
// (purchaseOrderPdf.js, LedgerExportSheet.js, labelPdf.js). It is a large
// library and Invoicing must not carry it in the main bundle for the sake of a
// button most page loads never press.
async function pdfDeps() {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;
  return { jsPDF, autoTable };
}

function drawMeta(doc, x, y, rows, width) {
  doc.setFontSize(8);
  let cursor = y;
  rows.forEach(([k, v]) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...MUTED_RGB);
    doc.text(String(k).toUpperCase(), x, cursor);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...INK_RGB);
    doc.text(doc.splitTextToSize(String(v == null ? '' : v), width - 110), x + 110, cursor);
    cursor += 12;
  });
  return cursor;
}

// Page N of M, written in a second pass for the reason LedgerExportSheet.js and
// purchaseOrderPdf.js both write it that way: the total is only knowable once
// every table has been laid out, and "Page 1" with no total tells a reader
// holding a printout nothing about whether pages are missing. On a batch, where
// the whole point is that many documents arrived as one file, it matters more.
function stampFooter(doc, label) {
  const pageCount = doc.internal.getNumberOfPages();
  const W = doc.internal.pageSize.width;
  const H = doc.internal.pageSize.height;
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(160, 160, 160);
    doc.text(label, 46, H - 24);
    doc.text(`Page ${p} of ${pageCount}`, W - 46, H - 24, { align: 'right' });
  }
}

// One merged PDF, one document per page. This is the artifact Coupa emails you
// later and Oracle makes you submit a separate report for.
export async function renderDocumentsPdf(documents, meta) {
  const { jsPDF, autoTable } = await pdfDeps();
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  const W = doc.internal.pageSize.width;
  const M = 46;

  documents.forEach((d, index) => {
    if (index > 0) doc.addPage();

    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...INK_RGB);
    doc.text(String(d.companyName || ''), M, 56);

    doc.setFontSize(15);
    doc.setTextColor(...ACCENT_RGB);
    doc.text(d.isCredit ? 'VENDOR CREDIT' : 'INVOICE', W - M, 56, { align: 'right' });

    doc.setDrawColor(...ACCENT_RGB);
    doc.setLineWidth(2);
    doc.line(M, 66, W - M, 66);

    const leftEnd = drawMeta(doc, M, 88, [
      ['Vendor', d.vendorName],
      ['Reference', d.invoiceRef],
      ['Document date', d.invoiceDate ? documentDate(d.invoiceDate) : '—'],
      ['Due', d.dueDate ? documentDate(d.dueDate) : (d.isCredit ? 'n/a' : 'Not set')],
    ], (W - M * 2) / 2);

    const rightEnd = drawMeta(doc, M + (W - M * 2) / 2, 88, [
      ['Branch', d.branchName || '—'],
      ['Order', d.poRef || d.requestNumber || '—'],
      ['Job', d.workOrderRef || '—'],
      ['Terms', d.paymentTerms || '—'],
    ], (W - M * 2) / 2);

    // The three figures, side by side and never blended. Gate screen 3's
    // caption, and #279's defect class stated as a table shape.
    autoTable(doc, {
      head: [['Item', 'SKU', 'Ordered', 'Received', 'Invoiced', 'Amount']],
      body: d.items.map(l => [
        l.label,
        l.sku,
        `${l.ordered}${l.uom ? ' ' + l.uom : ''}`,
        l.received == null ? 'none' : String(l.received),
        String(l.invoiced),
        money(l.amount),
      ]),
      startY: Math.max(leftEnd, rightEnd) + 14,
      margin: { left: M, right: M },
      styles: { fontSize: 8.5, cellPadding: 4.5, font: 'helvetica', textColor: INK_RGB },
      headStyles: { fillColor: INK_RGB, textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
      alternateRowStyles: { fillColor: [250, 250, 249] },
      columnStyles: {
        2: { halign: 'right' }, 3: { halign: 'right' },
        4: { halign: 'right' }, 5: { halign: 'right', fontStyle: 'bold' },
      },
    });

    let y = doc.lastAutoTable.finalY + 16;

    const totals = [
      ['Subtotal', money(d.subtotal)],
      ...(d.tax ? [['Tax', money(d.tax)]] : []),
      [d.isCredit ? 'Credit total' : 'Invoice total', money(d.total)],
      ...(d.creditedAmount ? [['Credited back', '-' + money(d.creditedAmount)]] : []),
    ];
    doc.setFontSize(9);
    totals.forEach(([k, v]) => {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...MUTED_RGB);
      doc.text(k, W - M - 120, y, { align: 'right' });
      doc.setTextColor(...INK_RGB);
      doc.text(v, W - M, y, { align: 'right' });
      y += 13;
    });

    y += 4;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...MUTED_RGB);
    doc.text(d.isCredit ? 'CREDIT DUE BACK' : 'NET DUE', W - M - 120, y, { align: 'right' });
    doc.setFontSize(14);
    doc.setTextColor(...INK_RGB);
    doc.text(money(d.netDue), W - M, y, { align: 'right' });
    y += 26;

    // An unresolved discrepancy travels ON the document. It is the single fact
    // that changes what a person does with the page they are holding, and a
    // payables document that stayed silent about it would be actively
    // misleading rather than merely incomplete.
    if (d.discrepancyFlagged && !d.discrepancyResolved) {
      doc.setFillColor(255, 244, 250);
      doc.setDrawColor(...ACCENT_RGB);
      doc.setLineWidth(1);
      doc.rect(M, y, W - M * 2, 34, 'FD');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...ACCENT_RGB);
      doc.text('UNRESOLVED DISCREPANCY', M + 12, y + 14);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...INK_RGB);
      doc.text('This invoice did not match its order or its receipt. Do not pay without reviewing it.', M + 12, y + 27);
    }
  });

  stampFooter(doc, [meta?.companyName, meta?.scopeLabel].filter(Boolean).join(' · '));
  return doc;
}

// Oracle's artifact: a listing you read, rather than a stack you file.
export async function renderRegisterPdf(register, meta) {
  const { jsPDF, autoTable } = await pdfDeps();
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
  const W = doc.internal.pageSize.width;
  const M = 40;

  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK_RGB);
  doc.text(String(meta?.companyName || ''), M, 50);

  doc.setFontSize(15);
  doc.setTextColor(...ACCENT_RGB);
  doc.text('PAYABLES REGISTER', W - M, 50, { align: 'right' });

  doc.setDrawColor(...ACCENT_RGB);
  doc.setLineWidth(2);
  doc.line(M, 60, W - M, 60);

  const afterMeta = drawMeta(doc, M, 80, meta?.describe || [], 360);

  // One body, with a subtotal row after each vendor's block, rather than one
  // table per vendor. autoTable paginates a single table correctly across pages
  // and cannot orphan a subtotal from the rows it totals.
  const body = [];
  register.vendors.forEach(v => {
    v.rows.forEach(r => {
      body.push([
        v.vendorName,
        r.kind,
        r.invoiceRef,
        r.invoiceDate ? documentDate(r.invoiceDate) : '—',
        r.dueDate ? documentDate(r.dueDate) : '—',
        r.poRef || r.requestNumber || '—',
        r.branchName || '—',
        r.flagged ? 'Flagged' : '',
        (r.isCredit ? '-' : '') + money(r.amount),
      ]);
    });
    body.push([
      { content: `${v.vendorName} — subtotal`, colSpan: 8, styles: { fontStyle: 'bold', halign: 'right' } },
      { content: money(v.subtotal), styles: { fontStyle: 'bold', halign: 'right' } },
    ]);
  });

  autoTable(doc, {
    head: [['Vendor', 'Type', 'Reference', 'Date', 'Due', 'Order', 'Branch', 'Status', 'Amount']],
    body,
    startY: afterMeta + 10,
    margin: { left: M, right: M },
    styles: { fontSize: 8, cellPadding: 4, font: 'helvetica', textColor: INK_RGB },
    headStyles: { fillColor: INK_RGB, textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
    alternateRowStyles: { fillColor: [250, 250, 249] },
    columnStyles: { 8: { halign: 'right' } },
  });

  let y = doc.lastAutoTable.finalY + 20;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...MUTED_RGB);
  doc.text('NET TOTAL', W - M - 140, y, { align: 'right' });
  doc.setFontSize(15);
  doc.setTextColor(...INK_RGB);
  doc.text(money(register.grand), W - M, y, { align: 'right' });

  // Say what the net is made of. A single figure derived from invoices minus
  // credits is not self-explanatory, and an accountant reading it needs to know
  // the credits were netted rather than dropped.
  if (register.creditCount) {
    y += 16;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...MUTED_RGB);
    doc.text(
      `${register.invoiceCount} invoice${register.invoiceCount === 1 ? '' : 's'} less `
      + `${register.creditCount} vendor credit${register.creditCount === 1 ? '' : 's'}. Credits are subtracted, not listed separately.`,
      W - M, y, { align: 'right' }
    );
  }

  stampFooter(doc, [meta?.companyName, 'Payables register', meta?.scopeLabel].filter(Boolean).join(' · '));
  return doc;
}

// Hand the file to the browser. Lifted from downloadPurchaseOrderPdf, including
// the deferred revoke -- Safari has a documented habit of cancelling the
// download if the object URL dies in the same frame.
export function savePdf(doc, filename) {
  const url = URL.createObjectURL(doc.output('blob'));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---------------------------------------------------------------------------
// D9 -- the audit trail
// ---------------------------------------------------------------------------

// Written AFTER the file is produced and best-effort, which is ledgerExport.js's
// rule and its reasoning holds unchanged: an export that failed must not be
// recorded as one that happened, and failing to log must never cost somebody the
// file they asked for.
//
// `surface` is 'invoicing' rather than 'inventory_ledger'. That column was
// deliberately left free-form when the table was built, precisely so a new
// surface would not need a migration -- see #423, which found the same table's
// `format` column had NOT been given that treatment.
export async function logBatchExport({ profile, layout, filters, documentCount }) {
  try {
    await supabase.from('export_log').insert({
      exported_by: profile?.id || null,
      exported_by_name_snapshot: profile?.full_name || 'Unknown',
      surface: 'invoicing',
      layout,
      format: 'pdf',
      filters,
      row_count: documentCount,
      included_cost: true,
    });
  } catch {
    /* best-effort, deliberately */
  }
}
